import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { currentDomain, currentPath } from '../Index';
import { usePromotionsData } from '../data/PromotionData';
import { useNavigate } from 'react-router-dom';
import useAppPromotionTheme from '../hooks/useAppPromotionTheme';
import PromotionSkeletonDetails from '../data/PromotionSkeletonDetails';

export default function VertexPlay({ isLightMode }) {
    const isAppPromo = useAppPromotionTheme(isLightMode);
    const { promotionsData, isPromotionDataReady } = usePromotionsData();
    const data = useMemo(
        () => promotionsData.find((promo) => promo.title === 'Vertex Play'),
        [promotionsData]
    );
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 841);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (!isPromotionDataReady) return;

        if (!data) {
            navigate('/not-found', { replace: true });
        }
    }, [isPromotionDataReady, data, navigate]);

    if (!isPromotionDataReady) {
        return <PromotionSkeletonDetails />;
    }

    if (!data) {
        return null;
    }

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title> Vertex Play </title>
                <link rel="icon" href="/images/fav.webp" sizes="32x32" />
                <meta name="description" content="ddd" />
                <link rel="canonical" href={`${currentDomain}${currentPath}`} />
            </Helmet>
            <div className="promotion-page-main all-page-main-container-min-height">
                <div className="promotion-page-container main-container-global-adjusted-padding">
                    <div className="promotion-tnc-page">
                        <div className="promotion-page-breadcrumb">
                            <a
                                href={
                                    isAppPromo
                                        ? '/promotion/'.replace(
                                              '/promotion',
                                              '/app-promotion'
                                          )
                                        : '/promotion/'
                                }
                            >
                                Promotions
                            </a>
                            / <span> {data.title} </span>
                        </div>
                        <div className="promotion-tnc-banner">
                            <img
                                src={isMobile ? data.imgSrc : data.bannerSrc}
                                alt={`FGH${data.title}`}
                            />
                            <div className="promotion-tnc-content">
                                <div className="promotion-tnc-info">
                                    <div
                                        className={`promotion-indication-message ${data.promotionMessage.class}`}
                                    >
                                        {data.promotionMessage.text}
                                    </div>
                                    <h1>Vertex Play</h1>
                                    <h2>Leaderboard Card Rewards Event</h2>
                                    <div className="promotion-tnc-period">
                                        <strong> Event Period: </strong>
                                        <span>
                                            October 23 - October 29, 2026
                                        </span>
                                    </div>
                                </div>
                                <div className="promotion-tnc-mechanic-content">
                                    <strong>
                                        {' '}
                                        General Terms & Conditions{' '}
                                    </strong>
                                    <ol>
                                        <li>
                                            This activity is available to all
                                            registered players on VertexPlay
                                            platform using designated currencies
                                            (CNY, PHP, MYR, MYR1, VND, THB).
                                        </li>
                                        <li>
                                            Daily settlement times and activity
                                            data calculations are based on the
                                            local timezone of the player's
                                            account currency.
                                        </li>
                                        <li>
                                            Any illegal betting, arbitrage,
                                            multi-accounting, fraud, or
                                            violation of fair play rules will
                                            result in immediate disqualification
                                            and forfeiture of rewards/cards.
                                        </li>
                                        <li>
                                            VertexPlay reserves the right to
                                            modify, suspend, or terminate this
                                            event at any time, as well as the
                                            right of final interpretation.
                                        </li>
                                    </ol>
                                    <strong>
                                        {' '}
                                        Event Rewards and Distribution{' '}
                                    </strong>
                                    <ol>
                                        <li>
                                            Reward Type: In-game Cards (Super
                                            Free Game Card, Mega Free Game Card,
                                            Free Game Card) distributed based on
                                            rank and currency.
                                        </li>
                                        <li>
                                            Distribution: Rewards are
                                            distributed directly to the winners'
                                            account balance/in-game bag after
                                            daily settlement.
                                        </li>
                                        <li>Card Usage Rules:</li>
                                        <ul>
                                            <li>
                                                Designated Game: Issued cards
                                                can only be activated within
                                                Golden Mahjong.
                                            </li>
                                            <li>
                                                Trigger Feature: Activated from
                                                the in-game bag to directly
                                                trigger Free Game, Mega Free
                                                Game, or Super Free Game
                                                features.
                                            </li>
                                            <li>
                                                Exclusivity: Card rewards are
                                                event-exclusive and valid only
                                                during the designated event
                                                period.
                                            </li>
                                        </ul>
                                    </ol>
                                    <strong>
                                        Leaderboard Mechanics & Qualification
                                    </strong>
                                    <div className="table-wrapper">
                                        <table
                                            className="promotion-tnc-table"
                                            style={{ minWidth: '0' }}
                                        >
                                            <thead>
                                                <th>Leaderboard</th>
                                                <th>Qualification</th>
                                                <th>Ranking Criteria</th>
                                                <th>Settlement</th>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Dragon Rank</td>
                                                    <td>
                                                        Win Multiplier &gt; 50x
                                                    </td>
                                                    <td>
                                                        Highest Single Spin Win
                                                        Amount
                                                    </td>
                                                    <td>Daily Settlement</td>
                                                </tr>
                                                <tr>
                                                    <td>Tiger Rank</td>
                                                    <td>
                                                        Win Multiplier &gt; 50x
                                                    </td>
                                                    <td>
                                                        Highest Single Spin
                                                        Multiplier (Win ÷
                                                        Bet){' '}
                                                    </td>
                                                    <td>Daily Settlement</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <strong>PHP Currency Rewards</strong>
                                    <div className="table-wrapper">
                                        <table
                                            className="promotion-tnc-table"
                                            style={{ minWidth: '0' }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Leaderboard</th>
                                                    <th>Rank Tier</th>
                                                    <th>Server Bet</th>
                                                    <th>Card Type</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td rowSpan="7">
                                                        Dragon Rank
                                                    </td>
                                                    <td>1</td>
                                                    <td>100</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>2 - 3</td>
                                                    <td>50</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>4 - 6</td>
                                                    <td>30</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>7 - 10</td>
                                                    <td>20</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>11 - 20</td>
                                                    <td>10</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>21 - 50</td>
                                                    <td>5</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>51 - 100</td>
                                                    <td>1</td>
                                                    <td>
                                                        Super Free Game Card
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <table
                                            className="promotion-tnc-table"
                                            style={{ minWidth: '0' }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>Leaderboard</th>
                                                    <th>Rank Tier</th>
                                                    <th>Server Bet</th>
                                                    <th>Card Type</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td rowSpan="5">
                                                        Tiger Rank
                                                    </td>
                                                    <td>1 - 3</td>
                                                    <td>20</td>
                                                    <td>Mega Free Game Card</td>
                                                </tr>
                                                <tr>
                                                    <td>4 - 10</td>
                                                    <td>10</td>
                                                    <td>Mega Free Game Card</td>
                                                </tr>
                                                <tr>
                                                    <td>11 - 50</td>
                                                    <td>5</td>
                                                    <td>Mega Free Game Card</td>
                                                </tr>
                                                <tr>
                                                    <td>51 - 100</td>
                                                    <td>2</td>
                                                    <td>Mega Free Game Card</td>
                                                </tr>
                                                <tr>
                                                    <td>101 - 200</td>
                                                    <td>1</td>
                                                    <td>Free Game Card</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
